import { useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/types/RootState";
import globalSearchClient from "@/apis/globalSearchClient";
import GlobalUtils from "@/utils/GlobalUtils";
import SearchItem, { translateSearchCategory } from "./SearchItem";
import SearchInput from "./SearchInput";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import IcomoonIcon from "@icons/IcomoonIcon";
import SntTargetIcon from "@icons/SntTargetIcon";
import SntSearchIcon from "@icons/SntSearchIcon";
import SntInOrientationIcon from "@icons/SntInOrientationIcon";
import SntPartnersIcon from "@icons/SntPartnersIcon";
import SntOrgBrandIcon from "@/cleanup/widgets/images/SntOrgBrandIcon";
import { DeviceHomeRoute } from "@/cleanup/utils/routesUtil/routesUtil";
import GeozoneImageDefault from "@images/default/geozone_default.svg";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { hasFeature } from "@/hooks/useFeatures";
import { hasPermission } from "@/hooks/usePermissions";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { UserPermission } from "@/types/generated/UserPermission";
import {
  SearchIndexItem,
  SearchHistoryItem,
  SearchItemData,
  SearchDataCategory,
} from "./types";

interface SearchPanelProps {
  searchText: string | null;
  closePopup: () => void;
  // When the header trigger is the compact/mobile magnifier icon there is no
  // search field in the header, so render one inside the panel instead.
  showSearchInput?: boolean;
}

/** The rail entry that shows every group at once — never filtered out. */
const RAIL_ALL = "ALL";
/** The rail entry for the recently-viewed pseudo-group (empty query). */
const RAIL_RECENT = "RECENT";
/** Prefix of a rail entry standing for one backend search category. */
const RAIL_CATEGORY_PREFIX = "cat:";

function SearchPanel({ searchText, closePopup, showSearchInput }: SearchPanelProps) {
  const [query, setQuery] = useState<string | null>(null);
  const [isRecent, setIsRecent] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [searchData, setSearchData] = useState<SearchDataCategory[]>();
  // Which rail entry is selected: RAIL_ALL, RAIL_RECENT, or "cat:<category>".
  const [railFilter, setRailFilter] = useState<string>(RAIL_ALL);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Index of the keyboard-highlighted `.gb-kb-row`. Held in a ref and applied
  // to the DOM directly: the highlight has to span every group, and the groups
  // own their own row rendering, so threading an active index through each of
  // them would mean prop-drilling into SearchItem for a purely visual concern.
  const kbIndexRef = useRef(-1);
  // The header passes a fresh closePopup every render, so hold it in a ref and
  // let the key listener subscribe once instead of on every keystroke.
  const closePopupRef = useRef(closePopup);
  closePopupRef.current = closePopup;
  // Partners are system-owned: their detail page is only reachable with the system org
  // selected, which is the viewer's own org here (global search is a system-user action).
  // Keep it in the URL so the sidebar menu doesn't blank out on navigation (STIC-15687).
  const ownOrgId = useSelector((state: RootState) => state.user?.organisation?.id);
  // Same 767px breakpoint the rail's `d-md-block` uses, so this is exactly
  // "the rail is off screen" — see App.tsx, which dispatches it from that query.
  const { isMobile } = useSelector((state: RootState) => state.all);

  useEffect(() => {
    setQuery(searchText);
  }, [searchText]);

  // A new query invalidates both the rail selection (its categories are about
  // to change) and the keyboard highlight (its rows are about to be replaced).
  useEffect(() => {
    setRailFilter(RAIL_ALL);
    kbIndexRef.current = -1;
  }, [query]);

  // Up/Down move a highlight across the rendered rows while focus stays in the
  // search field, Enter opens the highlighted row (the first one when nothing
  // is highlighted yet), Escape closes the panel. The listener is on window
  // because focus belongs to the input, which lives in the header, not here —
  // and the panel is only mounted while open, so it cannot swallow keys from
  // the rest of the app.
  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        closePopupRef.current();
        return;
      }
      if (ev.key !== "ArrowDown" && ev.key !== "ArrowUp" && ev.key !== "Enter") {
        return;
      }
      const rows = panelRef.current?.querySelectorAll<HTMLElement>(".gb-kb-row");
      if (!rows || rows.length === 0) return;
      if (ev.key === "Enter") {
        ev.preventDefault();
        const index = kbIndexRef.current;
        rows[index >= 0 && index < rows.length ? index : 0].click();
        return;
      }
      // Stop the caret from jumping to either end of the query while browsing.
      ev.preventDefault();
      rows.forEach((row) => row.classList.remove("kb-active"));
      let next = kbIndexRef.current + (ev.key === "ArrowDown" ? 1 : -1);
      if (next < 0) next = rows.length - 1;
      if (next >= rows.length) next = 0;
      kbIndexRef.current = next;
      rows[next].classList.add("kb-active");
      rows[next].scrollIntoView({ block: "nearest" });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useDebouncedEffect(() => loadData(query), 500, [query]);

  const loadData = (search: string | null) => {
    if (search) {
      setSearchData([]);
      setIsRecent(false);
      globalSearchClient.search(search).then(({ data }: { data: SearchIndexItem[] }) => {
        drawDataSearch(data);
      });
    } else if (!search) {
      setSearchData([]);
      setIsRecent(true);
      setIsNotFound(false);
      globalSearchClient
        .getSearchHistory()
        .then(({ data }: { data: SearchHistoryItem[] }) => {
          drawRecentViewed(data);
        });
    }
  };

  const drawDataSearch = (data: SearchIndexItem[]) => {
    const map: { [key: string]: SearchIndexItem[] } = {};

    if (data.length === 0) {
      setIsNotFound(true);
      return;
    }
    setIsNotFound(false);
    for (let index = 0; index < data.length; index++) {
      const item = data[index];
      if (map[item.category]) {
        const arr = map[item.category];
        arr.push(item);
        map[item.category] = arr;
      } else {
        const arr1: SearchIndexItem[] = [];
        arr1.push(item);
        map[item.category] = arr1;
      }
    }
    const items = Object.entries(map);
    const listData: { name: string; dataSearch: SearchIndexItem[] }[] = [];
    for (let index = 0; index < items.length; index++) {
      const element = items[index];
      const categorySearch = { name: element[0], dataSearch: element[1] };
      listData.push(categorySearch);
    }

    const searchList: { name: string; dataSearch: SearchIndexItem[] }[] = [];
    for (let index = 0; index < listData.length; index++) {
      const element = listData[index];
      searchList.push(element);
    }
    const datasHandle: SearchDataCategory[] = [];
    for (let i = 0; i < searchList.length; i++) {
      const value = searchList[i];
      const searchItems: SearchItemData[] = [];
      for (let index = 0; index < value?.dataSearch.length; index++) {
        const element = value?.dataSearch[index];
        const obj = getData(element);
        searchItems.push(obj);
      }
      const searchItem = { name: value.name, listItem: searchItems };
      datasHandle.push(searchItem);
    }
    setSearchData(datasHandle);
  };

  const drawRecentViewed = (data: SearchHistoryItem[]) => {
    const searchList: SearchItemData[] = [];
    for (let index = 0; index < data.length; index++) {
      const item = data[index];
      const obj = getData(item.searchIndex);
      searchList.push(obj);
    }
    const searchDatas: SearchDataCategory[] = [];
    searchDatas.push({
      name: translate(LanguageKey.v41_recent_viewed_key),
      listItem: searchList,
    });
    setSearchData(searchDatas);
  };

  const getData = (data: SearchIndexItem): SearchItemData => {
    let link = "";
    let icon_class: ReactNode = "";
    let category = "";
    const element: ReactNode = null;
    let entityType = "";
    let resourceType = "image";

    const _suffix = "?";

    // Ensure data has title field (API might return it)
    const enrichedData: SearchIndexItem = {
      ...data,
      title: data.title || "",
    };

    switch (data.category) {
      case "Asset":
        link = DeviceHomeRoute.getUpdatePage({
          id: data.entityId,
          org: data.orgId,
        });

        icon_class = (
          <img
            src="/images/device/tracker_default.svg"
            alt=""
            style={{ width: 30, height: 30 }}
          />
        );
        category = translate(LanguageKey.assets_key);
        entityType = "device";
        break;

      case "AlertRule":
        link =
          "/manage_alerts_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = <IcomoonIcon icon="warning" size="30"></IcomoonIcon>;
        category = translate(LanguageKey.sidebar_admin_alerts_key);
        break;

      case "Geozone":
        link =
          "/geozone_individual" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = (
          <img
            src={GeozoneImageDefault}
            alt=""
            style={{ width: 30, height: 30 }}
          />
        );
        category = translate(LanguageKey.geozones_key);
        entityType = "geozone";
        break;

      case "Infrastructure":
        link =
          "/infrastructures_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = <SntTargetIcon size={30}></SntTargetIcon>;
        category = translate(LanguageKey.infrastructure_key);
        break;

      case "User":
        link =
          "/users_admin_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = <IcomoonIcon icon="users" size="30"></IcomoonIcon>;
        category = translate(LanguageKey.sidebar_admin_users_key);
        entityType = "user";
        resourceType = "avatar";
        break;

      case "Organization":
        if (hasPermission(UserPermission.ORGANIZATION_MANAGE)) {
          link =
            "/organization_update" + _suffix + "org=" + data.entityId;
        } else {
          link =
            "/organization_update" + _suffix + "org=" + data.entityId;
        }
        icon_class = (
          <SntOrgBrandIcon
            domain={data.webDomain}
            name={data.title}
            size={30}
          />
        );
        category = translate(LanguageKey.organization_key);
        break;

      case "Partner":
        link =
          "/partners_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          (ownOrgId ? "&org=" + ownOrgId : "");
        icon_class = <SntPartnersIcon size={30} />;
        category = translate(LanguageKey.partner_partners_key);
        break;
      case "Sensor":
        link =
          "/ble_sensor_update" +
          _suffix +
          "type=view&id=" +
          data.entityId +
          "&org=" +
          data.orgId;
        icon_class = <SntInOrientationIcon size={30} />;
        category = translate(LanguageKey.sidebar_ex_ble_sensor_title_key);
        break;
      default:
        break;
    }

    let img: ReactNode = "";
    if (data.iconPath) {
      img = (
        <img
          style={{ width: 30, height: 30 }}
          src={GlobalUtils.getFile(
            entityType,
            data.entityId,
            resourceType,
            data.iconPath,
            "small"
          )}
          alt=""
        />
      );
    } else {
      img = (
        <span style={{ paddingTop: 5 }}>
          {icon_class}
        </span>
      );
    }

    return {
      link: link,
      img: img,
      category: category,
      element: element,
      data: enrichedData,
    };
  };

  // The Geozone feature/permission gate the panel has always applied, hoisted
  // out of the JSX so the rail counts exactly the groups that get rendered.
  const visibleSearchData = useMemo(
    () =>
      (searchData ?? []).filter(
        (item) =>
          item.name !== "Geozone" ||
          (hasFeature(FeatureKey.geozone_db) &&
            hasPermission(UserPermission.MENU_HOME_GEOZONE_OVERVIEW))
      ),
    [searchData]
  );

  const railItems = useMemo(() => {
    if (isRecent) {
      // The empty-query state is a single pseudo-group of recently viewed items.
      return visibleSearchData.length > 0
        ? [
            {
              key: RAIL_RECENT,
              label: translate(LanguageKey.v41_recent_viewed_key),
              count: visibleSearchData[0].listItem.length,
            },
          ]
        : [];
    }
    return visibleSearchData.map((category) => ({
      key: `${RAIL_CATEGORY_PREFIX}${category.name}`,
      label: translateSearchCategory(category.name),
      count: category.listItem.length,
    }));
  }, [visibleSearchData, isRecent]);

  const filteredSearchData = useMemo(
    () =>
      visibleSearchData.filter((item) =>
        railFilter === RAIL_ALL
          ? true
          : isRecent
            ? railFilter === RAIL_RECENT
            : railFilter === `${RAIL_CATEGORY_PREFIX}${item.name}`
      ),
    [visibleSearchData, railFilter, isRecent]
  );

  // Asking the rail for one category is itself the "show me all of these"
  // gesture, so the group drops its four-item cap. Under "All results" the cap
  // stays: it is what keeps every category visible without scrolling past one.
  const showAllInGroup = railFilter.startsWith(RAIL_CATEGORY_PREFIX);

  // With the rail on screen, "view all matching …" is the same request as
  // clicking that category — so make it do exactly that, rather than growing
  // the group inline and leaving the rail pointing at "All results".
  const railIsOnScreen = !isMobile && railItems.length > 0;

  return (
    <div id="search-panel" className="gb-search-wide" ref={panelRef}>
      {showSearchInput && (
        <div style={{ padding: 10 }}>
          <div style={{ position: "relative" }}>
            <SearchInput onChange={(e) => setQuery(e)} autoFocus />
          </div>
        </div>
      )}
      <div className="gb-search-body">
        {/* One rail entry per result group. Hidden with nothing to filter (no
            results yet, or the not-found state), and on mobile, where the panel
            is only as wide as the viewport. */}
        {railItems.length > 0 && (
          <div className="gb-search-rail d-none d-md-block">
            <button
              type="button"
              className={`gb-rail-btn ${railFilter === RAIL_ALL ? "active" : ""}`}
              onClick={() => setRailFilter(RAIL_ALL)}
            >
              <span className="text-truncate">
                {translate(LanguageKey.all_results_key)}
              </span>
            </button>
            {railItems.map((item) => (
              <button
                type="button"
                key={item.key}
                className={`gb-rail-btn ${railFilter === item.key ? "active" : ""}`}
                onClick={() => setRailFilter(item.key)}
              >
                <span className="text-truncate">{item.label}</span>
                <span className="gb-rail-count">{item.count}</span>
              </button>
            ))}
          </div>
        )}
        <ul className="gb-search-item snt-scrollbar">
          {!isNotFound &&
            filteredSearchData.map((item, index) => (
              <SearchItem
                key={index}
                dataSearch={item}
                closePopup={() => closePopup()}
                isRecent={isRecent}
                strSearch={searchText || undefined}
                showAll={showAllInGroup}
                onViewAll={
                  railIsOnScreen
                    ? () => setRailFilter(`${RAIL_CATEGORY_PREFIX}${item.name}`)
                    : undefined
                }
              />
            ))}
          <div
            className="not-found text-center"
            style={{
              paddingTop: 30,
              display: `${isNotFound ? "block" : "none"}`,
              color: "#424B59",
            }}
          >
            <SntSearchIcon size={60} />
            <div style={{ fontSize: 16, fontWeight: "bold" }}>
              {translate(LanguageKey.v41_no_matching_search_results_key)}
            </div>
            <div className="fs-6">
              {translate(LanguageKey.v41_try_again_using_more_general_search_terms_key)}
            </div>
          </div>
        </ul>
      </div>
    </div>
  );
}

export default SearchPanel;
